import React, { useState, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { clientsAPI } from '../../../services/api';
import { useLocale } from '../../../contexts/LocaleContext';

export default function UserSelect({ 
  value, 
  onChange, 
  placeholder = 'Search trader…',
}) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (open && !fetched) {
      clientsAPI
        .getAll()
        .then((res) => {
          const list = res?.data?.clients || res?.clients ||
                      (Array.isArray(res?.data) ? res.data : []);
          setUsers(list);
          setFetched(true);
        })
        .catch(() => {});
    }
  }, [open, fetched]);

  const fullName = (u) =>
    [u.first_name, u.last_name].filter(Boolean).join(' ') ||
    u.email ||
    `#${u.id}`;

  const filtered = users.filter((u) =>
    fullName(u).toLowerCase().includes(query.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(query.toLowerCase()) ||
    String(u.id).includes(query)
  );

  const selected = value ? users.find((u) => u.id === value) : null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs flex items-center justify-between gap-2 hover:border-[var(--accent-primary)] transition-smooth"
      >
        <span className="truncate text-[var(--text-primary)]">
          {selected ? `${fullName(selected)} (#${selected.id})` : placeholder}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 shrink-0 text-[var(--text-tertiary)] transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg shadow-lg max-h-48 flex flex-col">
          <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-[var(--border-color)]">
            <Search className="w-3.5 h-3.5 text-[var(--text-tertiary)] shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('clients.nameOrEmail')}
              className="flex-1 text-xs outline-none bg-transparent text-[var(--text-primary)] placeholder-[var(--text-tertiary)]"
            />
          </div>

          <div className="overflow-y-auto flex-1">
            {filtered.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => {
                  onChange(u.id);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-secondary)] transition-smooth ${
                  value === u.id ? 'bg-[var(--accent-primary)]/10' : ''
                }`}
              >
                <span className="text-[var(--text-primary)] font-medium">{fullName(u)}</span>
                <span className="text-[var(--text-tertiary)] ml-1.5">#{u.id}</span>
                <span className="text-[var(--text-tertiary)] ml-1.5">{u.email}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-xs text-[var(--text-tertiary)] px-3 py-2">
                {fetched ? t('plugins.countrySelectNoMatch') : t('plugins.loadingUsers')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}